import * as React from "react";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Container from "@amzn/awsui-components-react/polaris/container";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Flashbar from "@amzn/awsui-components-react/polaris/flashbar";
import Spinner from "@amzn/awsui-components-react/polaris/spinner";
import Table from "@amzn/awsui-components-react/polaris/table";
import CopyToClipboard from "@amzn/awsui-components-react/polaris/copy-to-clipboard";
import { API } from "aws-amplify";
import { SelectOption } from "./feature-type-filter";
import { fetchAllConfigs } from "./cpcp-config-api";
import FeatureTypeFilters from "./FeatureTypeFilters";

interface ConfigItem {
  featureName: string;
  customerTypeName: string;
  description?: string;
}

interface AddedAdultProfile {
  personId?: string;
  profileId?: string;
  phoneNumber?: string;
}

interface CustomerResult {
  customerId?: string;
  email?: string;
  password?: string;
  personId?: string;
  profileId?: string;
  phoneNumber?: string;
  addedAdultProfile?: AddedAdultProfile;
  addressId?: string;
  featureName?: string;
  customerTypeName?: string;
  createdDate?: number;
  status?: string;
}

interface APIError extends Error {
  response?: { status: number };
}

interface State {
  configs: ConfigItem[];
  loading: boolean;
  claiming: boolean;
  error: React.ReactNode | null;
  filterFeature: SelectOption | null;
  filterType: SelectOption | null;
  customer: CustomerResult | null;
}

class GetCustomer extends React.Component<any, State> {
  constructor(props: any) {
    super(props);
    this.state = {
      configs: [],
      loading: true,
      claiming: false,
      error: null,
      filterFeature: null,
      filterType: null,
      customer: null,
    };
  }

  componentDidMount() { this.loadConfigs(); }

  loadConfigs = async () => {
    try {
      const configs = await fetchAllConfigs<ConfigItem>();
      this.setState({ configs, loading: false });
    } catch (e) {
      this.setState({ error: e instanceof Error ? e.message : "Failed to load configs", loading: false });
    }
  };

  handleClaim = async () => {
    const { filterFeature, filterType } = this.state;
    if (!filterFeature?.value || !filterType?.value) {
      this.setState({ error: "Select both Feature Name and Customer Type Name" });
      return;
    }
    this.setState({ claiming: true, error: null, customer: null });
    try {
      const result = await API.post("CpcpV2API",
        `/cpcp/v2/customer/${filterFeature.value}/${filterType.value}/claim`, {});
      this.setState({ customer: result, claiming: false });
    } catch (e) {
      this.setState({ error: this.getClaimError(e), claiming: false });
    }
  };

  private getClaimError = (e: unknown): React.ReactNode => {
    if (e instanceof Error && 'response' in e && (e as APIError).response?.status === 400) {
      return (
        <span>
          Please check your cached pharmacy customer type.<br/>
          If there are no cached pharmacy customers for the requested type, the provider might be failing to backfill data due to beta issues.<br/>
          Check the open beta issue at <a href="https://tiny.amazon.com/cl3awodk/tcorpamazissu" target="_blank" rel="noopener noreferrer">https://tiny.amazon.com/cl3awodk/tcorpamazissu</a>.<br/>
          For urgent debugging, contact <a href="https://t.corp.amazon.com/create/templates/b3ec6a65-4e7a-4a9b-8985-ff9b745835b6" target="_blank" rel="noopener noreferrer">https://t.corp.amazon.com/create/templates/b3ec6a65-4e7a-4a9b-8985-ff9b745835b6</a>
        </span>
      );
    }
    return e instanceof Error ? e.message : "Failed to claim customer";
  };

  render() {
    const { configs, loading, claiming, error, filterFeature, filterType, customer } = this.state;

    if (loading) return <Spinner size="large" />;

    const selectedConfig = filterFeature?.value && filterType?.value
      ? configs.find(c => c.featureName === filterFeature.value && c.customerTypeName === filterType.value)
      : null;

    return (
      <SpaceBetween size="l">
        <Header variant="h2">Get Customer</Header>

        {error && <Flashbar items={[{ type: "error", content: error, dismissible: true, onDismiss: () => this.setState({ error: null }) }]} />}

        <FeatureTypeFilters
          items={configs}
          selection={{ feature: filterFeature, type: filterType }}
          onChange={next => this.setState({ filterFeature: next.feature, filterType: next.type, customer: null, error: null })}
          placeholder="Select..."
        >
          <FormField label="&nbsp;">
            <Button variant="primary" onClick={this.handleClaim}
              loading={claiming}
              disabled={!filterFeature?.value || !filterType?.value}>
              Get Customer
            </Button>
          </FormField>
        </FeatureTypeFilters>

        {selectedConfig?.description && (
          <Box color="text-body-secondary">
            <b>Description:</b> {selectedConfig.description}
          </Box>
        )}

        {customer && (
          <Container
            header={
              <Header variant="h3"
                actions={
                  <CopyToClipboard
                    copyButtonText="Copy JSON"
                    copySuccessText="Copied!"
                    copyErrorText="Failed to copy"
                    textToCopy={JSON.stringify(customer, null, 2)}
                  />
                }>
                Claimed Customer
              </Header>
            }>
            {this.renderFields(customer, ['status', 'addedAdultProfile'])}
          </Container>
        )}

        {customer?.addedAdultProfile && hasAnyValue(customer.addedAdultProfile) && (
          <Container header={<Header variant="h3">Second Adult</Header>}>
            {this.renderFields(customer.addedAdultProfile)}
          </Container>
        )}
      </SpaceBetween>
    );
  }

  // One row per non-null field, with a copy button; skipKeys are left out.
  private renderFields = <T extends object>(record: T, skipKeys: (keyof T)[] = []): JSX.Element => (
    <Table
      columnDefinitions={[
        { id: "key", header: "Field", cell: (item: FieldRow) => <b>{item.key}</b>, width: 200 },
        { id: "value", header: "Value", cell: (item: FieldRow) => (
          <SpaceBetween size="xs" direction="horizontal">
            <Box fontWeight={['password', 'email', 'phoneNumber'].includes(item.key) ? "bold" : "normal"}>
              {item.display}
            </Box>
            <CopyToClipboard
              copyButtonText="Copy"
              copySuccessText="Copied!"
              copyErrorText="Failed"
              textToCopy={item.display}
              variant="icon"
            />
          </SpaceBetween>
        )},
      ]}
      items={(Object.entries(record) as [string, unknown][])
        .filter(([k, v]) => v != null && !skipKeys.includes(k as keyof T))
        .map(([key, value]) => ({ key, display: formatValue(value) }))}
      variant="embedded"
    />
  );
}

interface FieldRow {
  key: string;
  display: string;
}

const hasAnyValue = (record: object): boolean => Object.values(record).some(v => v != null);

// Plain objects are shown as JSON so a nested field never renders as "[object Object]";
// arrays keep String()'s "a,b" form, as before.
const formatValue = (value: unknown): string =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? JSON.stringify(value) : String(value);

export default GetCustomer;
